import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Card, Chip, CircularProgress, Divider, Sheet, Stack,
    Tab, TabList, TabPanel, Tabs, Tooltip, Typography, tabClasses,
} from '@mui/joy';
import { useNavigate, useParams } from 'react-router';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import { useAdvertiser } from '../../../AdvertiserContext';
import {
    APIAdsCampaignArchive, APIAdsCampaignDelete, APIAdsCampaignDetail,
    APIAdsCampaignPause, APIAdsCampaignResume, APIAdsCampaignSubmit,
    APIAdsCampaignUpdate, adsErrorText,
} from '../../../api_ads';
import AdStatusChip from '../../../components/Ads/AdStatusChip';
import AdModerationLog from '../../../components/Ads/AdModerationLog';
import AdCampaignStats from '../../../components/Ads/reports/AdCampaignStats';
import AdBidAdjustments from '../../../components/Ads/optimization/AdBidAdjustments';
import AdStrategyPanel from '../../../components/Ads/optimization/AdStrategyPanel';
import AdCampaignForm from '../../../components/Ads/AdCampaignForm';
import AdGroupsManager from '../../../components/Ads/AdGroupsManager';
import AdPlacementsPicker from '../../../components/Ads/AdPlacementsPicker';
import { useAdsAccount, useAdsPlacements } from '../../../components/Ads/useAdsData';
import AdCampaignInsights from '../../../components/Ads/AdCampaignInsights';

import {
    FORMAT_LABELS, OBJECTIVE_LABELS,
    PRICING_LABELS, STRATEGY_LABELS, can, formatMoney,
} from '../../../components/Ads/adsConstants';


function InfoRow({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.6 }}>
            <Typography level="body-sm" color="neutral">{label}</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600, textAlign: 'right' }}>
                {value ?? '—'}
            </Typography>
        </Stack>
    );
}


function CreativeCard({ creative }) {
    return (
        <Card variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box sx={{ flexShrink: 0 }}>
                    <AdCreativePreview creative={creative} format={creative.format} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                        <Typography level="title-sm">{creative.title}</Typography>
                        <AdStatusChip status={creative.moderation_status} kind="creative" />
                        <Chip size="sm" variant="outlined">
                            {FORMAT_LABELS[creative.format] || creative.format}
                        </Chip>
                    </Stack>

                    {creative.moderation_status === 'rejected' && (
                        <Alert color="danger" size="sm" sx={{ mt: 1 }}>
                            <Box>
                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                    {creative.rejection_reason_display || 'Отклонено'}
                                </Typography>
                                {creative.moderation_comment && (
                                    <Typography level="body-sm">{creative.moderation_comment}</Typography>
                                )}
                            </Box>
                        </Alert>
                    )}

                    <Box sx={{ mt: 1 }}>
                        <InfoRow label="Рекламодатель" value={creative.brand_name} />
                        <InfoRow label="Ссылка" value={creative.landing_url} />
                        <InfoRow label="Показы" value={creative.impressions_total ?? 0} />
                        <InfoRow label="Клики" value={creative.clicks_total ?? 0} />
                    </Box>
                </Box>
            </Stack>
        </Card>
    );
}


export default function AdsCampaignDetailPage() {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const { advertiserToken: token } = useAdvertiser();

    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(null);
    const [tab, setTab] = useState(0);

    // Черновик настроек: правки применяются только по кнопке «Сохранить»,
    // иначе каждый символ в поле бюджета уходил бы отдельным PATCH.
    const [settingsDraft, setSettingsDraft] = useState(null);
    const [placementsDraft, setPlacementsDraft] = useState([]);
    const { limits } = useAdsAccount();
    const { placements } = useAdsPlacements();

    const load = useCallback(async () => {
        if (!token || !campaignId) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdsCampaignDetail(token, campaignId);
            const loaded = res?.campaign || null;
            setCampaign(loaded);
            if (loaded) {
                setSettingsDraft({ ...loaded });
                setPlacementsDraft(
                    (loaded.campaign_placements || []).map((l) => l.placement_code)
                );
            }
        } catch (e) {
            if (e?.response?.status === 404) setError('Кампания не найдена');
            else setError(adsErrorText(e, 'Не удалось загрузить кампанию'));
        } finally {
            setLoading(false);
        }
    }, [token, campaignId]);

    useEffect(() => { load(); }, [load]);

    const handleAction = async (action) => {
        setNotice(null);
        setError(null);
        setBusy(action);
        try {
            if (action === 'submit') {
                const res = await APIAdsCampaignSubmit(token, campaignId);
                setNotice(res?.auto_approved
                    ? 'Кампания одобрена автоматически и запущена'
                    : 'Кампания отправлена на модерацию');
            } else if (action === 'pause') {
                await APIAdsCampaignPause(token, campaignId);
            } else if (action === 'resume') {
                await APIAdsCampaignResume(token, campaignId);
            } else if (action === 'archive') {
                await APIAdsCampaignArchive(token, campaignId);
            } else if (action === 'delete') {
                await APIAdsCampaignDelete(token, campaignId);
                navigate('/business');
                return;
            }
            await load();
        } catch (e) {
            const data = e?.response?.data;
            if (Array.isArray(data?.errors)) {
                setError(`Кампания не готова к отправке:\n• ${data.errors.join('\n• ')}`);
            } else {
                setError(adsErrorText(e));
            }
        } finally {
            setBusy(null);
        }
    };

    if (loading) {
        return (
            <Stack alignItems="center" sx={{ py: 8 }}>
                <CircularProgress />
            </Stack>
        );
    }

    if (!campaign) {
        return (
            <Box>
                <Alert color="danger" sx={{ mb: 2 }}>{error || 'Кампания не найдена'}</Alert>
                <Button
                    variant="outlined"
                    color="neutral"
                    startDecorator={<ArrowBackRoundedIcon />}
                    onClick={() => navigate('/business')}
                >
                    К списку кампаний
                </Button>
            </Box>
        );
    }

    const groups = campaign.groups || [];
    const allCreatives = groups.flatMap((g) => g.creatives || []);

    // Сервер разрешает менять структуру только в этих статусах
    const structureEditable = ['draft', 'rejected', 'paused'].includes(campaign.status);

    // У работающей кампании доступны только бюджет, ставки, расписание и дата конца
    const lockedFields = structureEditable
        ? []
        : ['objective', 'pricing_model', 'date_start'];

    const allowedFormats = [...new Set(
        placements
            .filter((p) => placementsDraft.includes(p.code))
            .map((p) => p.format)
    )];

    const saveSettings = async () => {
        setBusy('settings');
        setError(null);
        setNotice(null);
        try {
            const payload = {
                name: settingsDraft.name,
                daily_budget: Number(settingsDraft.daily_budget),
                pacing: settingsDraft.pacing,
                schedule: settingsDraft.schedule || {},
                freq_per_day: Number(settingsDraft.freq_per_day) || 5,
                freq_interval_sec: Number(settingsDraft.freq_interval_sec) || 600,
            };
            if (settingsDraft.total_budget) payload.total_budget = Number(settingsDraft.total_budget);
            if (settingsDraft.weekly_budget) payload.weekly_budget = Number(settingsDraft.weekly_budget);
            if (settingsDraft.max_avg_cpc) payload.max_avg_cpc = Number(settingsDraft.max_avg_cpc);
            if (settingsDraft.date_end) payload.date_end = settingsDraft.date_end;
            if (settingsDraft.pricing_model === 'cpc') {
                payload.bid_cpc = Number(settingsDraft.bid_cpc);
            } else {
                payload.bid_cpm = Number(settingsDraft.bid_cpm);
            }

            // Структурные поля отправляем, только если это разрешено статусом
            if (structureEditable) {
                payload.objective = settingsDraft.objective;
                payload.pricing_model = settingsDraft.pricing_model;
                payload.bid_strategy = settingsDraft.bid_strategy;
                payload.placements = placementsDraft;
                if (settingsDraft.date_start) payload.date_start = settingsDraft.date_start;
            }

            await APIAdsCampaignUpdate(token, campaignId, payload);
            setNotice('Настройки сохранены');
            await load();
        } catch (e) {
            const data = e?.response?.data;
            if (Array.isArray(data?.forbidden_fields)) {
                setError(`Эти поля нельзя менять у работающей кампании: ${data.forbidden_fields.join(', ')}`);
            } else {
                setError(adsErrorText(e));
            }
        } finally {
            setBusy(null);
        }
    };

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                sx={{ mb: 2 }}
            >
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        startDecorator={<ArrowBackRoundedIcon />}
                        onClick={() => navigate('/business')}
                    >
                        К списку
                    </Button>
                    <Divider orientation="vertical" />
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography level="h4">{campaign.name}</Typography>
                            <AdStatusChip
                                status={campaign.status}
                                systemPaused={campaign.system_paused}
                                systemPauseReason={campaign.system_pause_reason}
                            />
                        </Stack>
                        <Typography level="body-xs" color="neutral">
                            ID {campaign.id} · создана{' '}
                            {campaign.date_create
                                ? format(new Date(campaign.date_create), 'd MMMM yyyy', { locale: ru })
                                : '—'}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1}>
                    {can(campaign.status, 'submit') && (
                        <Button
                            startDecorator={<SendRoundedIcon />}
                            loading={busy === 'submit'}
                            onClick={() => handleAction('submit')}
                        >
                            На модерацию
                        </Button>
                    )}
                    {can(campaign.status, 'pause') && (
                        <Button
                            variant="soft"
                            color="neutral"
                            startDecorator={<PauseRoundedIcon />}
                            loading={busy === 'pause'}
                            onClick={() => handleAction('pause')}
                        >
                            Остановить
                        </Button>
                    )}
                    {can(campaign.status, 'resume') && (
                        <Button
                            variant="soft"
                            color="success"
                            startDecorator={<PlayArrowRoundedIcon />}
                            loading={busy === 'resume'}
                            onClick={() => handleAction('resume')}
                        >
                            Возобновить
                        </Button>
                    )}
                    {can(campaign.status, 'archive') && (
                        <Tooltip title="В архив" arrow>
                            <Button
                                variant="plain"
                                color="neutral"
                                loading={busy === 'archive'}
                                onClick={() => handleAction('archive')}
                            >
                                <Inventory2RoundedIcon />
                            </Button>
                        </Tooltip>
                    )}
                    {can(campaign.status, 'delete') && (
                        <Tooltip title="Удалить черновик" arrow>
                            <Button
                                variant="plain"
                                color="danger"
                                loading={busy === 'delete'}
                                onClick={() => handleAction('delete')}
                            >
                                <DeleteOutlineRoundedIcon />
                            </Button>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            {notice && <Alert color="success" sx={{ mb: 2 }}>{notice}</Alert>}
            {error && (
                <Alert color="danger" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>
            )}

            {campaign.status === 'rejected' && (
                <Alert color="danger" sx={{ mb: 2 }}>
                    <Box>
                        <Typography level="title-sm">
                            {campaign.rejection_reason_display || 'Кампания отклонена'}
                        </Typography>
                        {campaign.moderation_comment && (
                            <Typography level="body-sm">{campaign.moderation_comment}</Typography>
                        )}
                        <Typography level="body-sm" sx={{ mt: 0.5 }}>
                            Исправьте замечания и отправьте кампанию на модерацию повторно.
                        </Typography>
                    </Box>
                </Alert>
            )}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'transparent' }}>
                <TabList sx={{
                    mb: 2, borderRadius: 'md',
                    [`& .${tabClasses.root}.${tabClasses.selected}`]: {
                        bgcolor: 'primary.softBg', color: 'primary.700',
                    },
                }}>
                    <Tab>Статистика</Tab>
                    <Tab>Настройки</Tab>
                    <Tab>Группы и объявления ({allCreatives.length})</Tab>
                    <Tab>Оптимизация</Tab>
                    <Tab>История модерации</Tab>
                </TabList>

                {/* --- Статистика --- */}
                <TabPanel value={0} sx={{ p: 0 }}>
                    <AdCampaignInsights token={token} campaignId={campaignId} campaign={campaign} />
                    <AdCampaignStats campaignId={campaignId} />
                </TabPanel>

                {/* --- Настройки --- */}
                <TabPanel value={1} sx={{ p: 0 }}>
                    {settingsDraft && (
                        <Stack spacing={2}>
                            <AdCampaignForm
                                value={settingsDraft}
                                onChange={setSettingsDraft}
                                limits={limits}
                                selectedPlacements={placementsDraft}
                                lockedFields={lockedFields}
                            />

                            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                                <Typography level="title-sm" sx={{ mb: 1.5 }}>
                                    Площадки размещения
                                </Typography>
                                {structureEditable ? (
                                    <AdPlacementsPicker
                                        pricingModel={settingsDraft.pricing_model}
                                        selected={placementsDraft}
                                        onChange={setPlacementsDraft}
                                    />
                                ) : (
                                    <Stack direction="row" spacing={0.8} flexWrap="wrap">
                                        {(campaign.campaign_placements || []).map((link) => (
                                            <Chip key={link.id} variant="soft" color="primary">
                                                {link.placement_name}
                                            </Chip>
                                        ))}
                                        <Alert color="neutral" variant="soft" size="sm" sx={{ mt: 1 }}>
                                            Остановите кампанию, чтобы изменить набор площадок
                                        </Alert>
                                    </Stack>
                                )}
                            </Sheet>

                            <Stack direction="row" spacing={1}>
                                <Button
                                    loading={busy === 'settings'}
                                    onClick={saveSettings}
                                >
                                    Сохранить настройки
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="neutral"
                                    disabled={busy === 'settings'}
                                    onClick={() => {
                                        setSettingsDraft({ ...campaign });
                                        setPlacementsDraft(
                                            (campaign.campaign_placements || [])
                                                .map((l) => l.placement_code)
                                        );
                                    }}
                                >
                                    Отменить изменения
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </TabPanel>

                {/* --- Группы и объявления --- */}
                <TabPanel value={2} sx={{ p: 0 }}>
                    <AdGroupsManager
                        campaign={campaign}
                        allowedFormats={allowedFormats}
                        editable={structureEditable}
                        onChanged={load}
                    />
                </TabPanel>

                {/* --- Оптимизация --- */}
                <TabPanel value={3} sx={{ p: 0 }}>
                    <Stack spacing={3}>
                        <AdStrategyPanel campaign={campaign} />
                        <AdBidAdjustments campaign={campaign} editable={structureEditable} />
                    </Stack>
                </TabPanel>

                {/* --- История модерации --- */}
                <TabPanel value={4} sx={{ p: 0 }}>
                    <AdModerationLog campaignId={campaignId} />
                </TabPanel>
            </Tabs>
        </Box>
    );
}